import { SerializedNode, SerializedNodes } from "@craftjs/core";
import { useMemo } from "react";
import { ActiveStepType } from "@/src/shared/schemas";
import { setRules } from "../stores/useRulesStore";
import {Analytics} from "@/src/shared/models/analytics";
import {addStepAnalytic, updateComponentAnalytics} from "@/src/stores/useAnalyticsStore";

interface WebPageComponentDto extends SerializedNode {
  id: string;
  name: string;
  type: string;
  pageId: string;
  createdAt?: string;
  updatedAt?: string;
}

export const safeParce = (componentData: string, validate = false): any => {
  if (!componentData) return "";
  let ids = {};
  let rules = [];

  try {
    const parsedData = JSON.parse(componentData);

    if (parsedData.rules && parsedData.ids) {
      rules = parsedData.rules;
      ids = parsedData.ids;
    } else {
      ids = parsedData;
    }

    return { ids, rules, analytics: parsedData.analytics };
  } catch (e) {
    if (validate) {
      return "";
    }
  }
};

const reverseObject = (obj: any): any => {
  return Object.entries(obj).reduce((reversed: any, [key, value]: any) => {
    reversed[value] = key;
    return reversed;
  }, {});
};

export const denormalizeData = (
  normalizedData: WebPageComponentDto[],
  ids: Record<string, string>,
): SerializedNodes => {
  const result: SerializedNodes = {};
  const idMap: any = reverseObject(ids);
  const currentIds = normalizedData.map((component) => component.id);

  normalizedData.forEach((component: any) => {
    const type = safeParce(component.type);
    const key = idMap[component.id];
    result[key] = {
      ...component,
      type: type?.ids || type,
      nodes: component.nodes.reduce((acc: string[], nodeId: string) => {
        const id = idMap[nodeId];
        if (currentIds.includes(nodeId)) {
          acc.push(id);
        }
        return acc;
      }, []),
      parent: idMap[component.parent as string],
      custom: {
        ...component.custom,
        originalId: key,
      },
    };
  });

  return result;
};

export const useWebPageComponent = (
  data: Omit<ActiveStepType["page"], "id">,
): any => {
  return useMemo((): any => {
    const parsedData: any = safeParce(data.description || "", true);

    if (!parsedData || typeof parsedData === "string") {
      return {};
    }
    const { ids, rules, analytics } = parsedData;

    if (analytics) {
      const { stepAnalytics, componentAnalytics } = analytics as Analytics;
      stepAnalytics && addStepAnalytic(stepAnalytics);
      componentAnalytics?.length && updateComponentAnalytics(componentAnalytics);
    }

    setRules(rules);

    const page: any = denormalizeData(data.components as any, ids);
    if (Object.keys(page).length === 0) {
      return {};
    }
    return page;
  }, [data]);
};
